import React from "react";
import { Box, Button, Text, VStack, HStack, Badge } from "@chakra-ui/react";
import { format } from "date-fns";
import { TimeSlot, Physician, Location } from "../types";

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedSlot: TimeSlot | null;
  physicians: Physician[];
  locations: Location[];
  onConfirm: () => void;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  selectedSlot,
  physicians,
  locations,
  onConfirm,
}) => {
  if (!selectedSlot) return null;

  const physician = physicians.find((p) => p.id === selectedSlot.physicianId);
  const location = locations.find((l) => l.id === selectedSlot.locationId);

  const startTime = new Date(selectedSlot.startTime);
  const endTime = new Date(selectedSlot.endTime);

  if (!isOpen) return null;

  return (
    <Box
      position="fixed"
      top="0"
      left="0"
      right="0"
      bottom="0"
      bg="rgba(0, 0, 0, 0.5)"
      zIndex="1000"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Box
        bg="white"
        borderRadius="lg"
        maxW="md"
        w="100%"
        overflow="hidden"
        boxShadow="xl"
      >
        {/* Header */}
        <Box p={4} borderBottomWidth="1px" position="relative">
          <Text fontWeight="bold" fontSize="lg">
            Confirm Your Appointment
          </Text>
          <Button
            position="absolute"
            top={3}
            right={3}
            size="sm"
            variant="ghost"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </Button>
        </Box>

        {/* Body */}
        <Box p={4}>
          <VStack gap={4} align="stretch">
            <HStack justify="space-between">
              <Text fontWeight="bold">Date:</Text>
              <Text>{format(startTime, "EEEE, MMMM d, yyyy")}</Text>
            </HStack>

            <HStack justify="space-between">
              <Text fontWeight="bold">Time:</Text>
              <Text>
                {format(startTime, "h:mm a")} - {format(endTime, "h:mm a")}
              </Text>
            </HStack>

            <HStack justify="space-between">
              <Text fontWeight="bold">Physician:</Text>
              <Text>{physician?.name || "Unknown"}</Text>
            </HStack>

            <HStack justify="space-between">
              <Text fontWeight="bold">Specialty:</Text>
              <Text>{physician?.specialty || "Unknown"}</Text>
            </HStack>

            <HStack justify="space-between">
              <Text fontWeight="bold">Location:</Text>
              <Text>{location?.name || "Unknown"}</Text>
            </HStack>

            <Box height="1px" bg="gray.200" my={2} />

            <Text fontSize="sm" color="gray.600">
              By confirming this appointment, you agree to arrive 15 minutes
              before your scheduled time. You can reschedule or cancel this
              appointment up to 24 hours in advance.
            </Text>

            <Badge
              colorScheme="blue"
              alignSelf="flex-start"
              width="fit-content"
            >
              15-minute appointment
            </Badge>
          </VStack>
        </Box>

        {/* Footer */}
        <Box
          p={4}
          borderTopWidth="1px"
          display="flex"
          justifyContent="flex-end"
          gap={3}
        >
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme="blue" onClick={onConfirm}>
            Confirm Appointment
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ConfirmationModal;
